import React, { type ReactNode } from 'react';
import { LockIcon } from '@mentimeter/ragnar-visuals';
import { useTranslations } from 'next-intl';
import { PaymentMethodType } from '@mentimeter/http-clients';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Link } from '@mentimeter/ragnar-ui/link';
import { Skeleton } from '@mentimeter/ragnar-ui/skeleton';
import { Text } from '@mentimeter/ragnar-ui/text';

const TermsLink = (chunks: ReactNode) => (
  <Link color="text" href="/terms" target="_blank" rel="noopener noreferrer">
    {chunks}
  </Link>
);

export const PurchaseSummaryLoading = ({
  paymentMethod,
}: {
  paymentMethod: PaymentMethodType | undefined;
}) => {
  const t = useTranslations('purchasing.legacy.checkoutUiPackage');
  const isBankTransferPaymentMethod =
    paymentMethod === PaymentMethodType.BankAccount ||
    paymentMethod === PaymentMethodType.VirtualAccount;

  return (
    <>
      <Box flexDirection="row" width="100%" mb="space2">
        <Box width={200}>
          <Skeleton variant="text" fontSize="100" width="60%" />
          <Skeleton variant="text" fontSize="87.5" width="70%" />
        </Box>
        <Box flex="1 1 auto" ml="space1" alignItems="end">
          <Skeleton variant="text" width="100%" />
        </Box>
      </Box>
      <Box flexDirection="row" width="100%">
        <Box width={200}>
          <Skeleton variant="text" fontSize="100" width="60%" />
          <Skeleton variant="text" fontSize="87.5" width="70%" />
        </Box>
        <Box flex="1 1 auto" ml="space1" alignItems="end">
          <Skeleton variant="text" width="100%" />
        </Box>
      </Box>
      <Box flexDirection="row" mt="space3" width="100%">
        <Skeleton variant="text" width={200} />
        <Box flexDirection="row" mt="space3" width="100%">
          <Skeleton variant="text" width={150} />
          <Box flex="1 1 auto" alignItems="end">
            <Skeleton variant="text" />
          </Box>
        </Box>
      </Box>
      <Box
        mt="space6"
        borderTopWidth="1px"
        borderStyle="dotted"
        width="100%"
        borderColor="borderStrong"
      />
      <Box flexDirection="row" width="100%" mt="space6" alignItems="center">
        <Skeleton variant="text" width="50%" />
        <Box flex="1 1 auto" alignItems="end">
          <Skeleton variant="text" />
        </Box>
      </Box>
      <Box width="100%" mt="space6">
        <Skeleton variant="rectangle" width="100%" height="58px" />
      </Box>
      <Button
        width="100%"
        variant="primary"
        iconLeading={LockIcon}
        mt="space6"
        disabled
      >
        {isBankTransferPaymentMethod
          ? t('generate_invoice')
          : t('confirm_and_pay_button')}
      </Button>
      <Text fontSize="75" color="textWeaker" mt="space2">
        {t.rich('confirm_terms', { termsLink: TermsLink })}
      </Text>
    </>
  );
};
